import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaMapMarkerAlt, FaEnvelope, FaPhone, FaPaperPlane, FaGithub, FaLinkedin, FaFacebook, FaTwitter, FaTelegram, FaWhatsapp } from 'react-icons/fa';
import { toast } from 'react-toastify';
import API from '../api/axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import FAQSection from '../components/FAQSection';

const ContactPage = () => {
  const [profile, setProfile] = useState(null);
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    Promise.allSettled([API.get('/profile'), API.get('/settings/public')])
      .then(([p, s]) => {
        if (p.status === 'fulfilled') setProfile(p.value.data);
        if (s.status === 'fulfilled') setSettings(s.value.data);
      });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try { await API.post('/messages', form); toast.success('Message sent successfully!'); setForm({ name: '', email: '', subject: '', message: '' }); }
    catch { toast.error('Failed to send. Please try again.'); }
    setSending(false);
  };

  const socialIcons = { github: <FaGithub />, linkedin: <FaLinkedin />, facebook: <FaFacebook />, twitter: <FaTwitter />, telegram: <FaTelegram />, whatsapp: <FaWhatsapp /> };
  const socials = { ...profile?.social, ...settings?.social };

  return (
    <>
      <Navbar />
      <div style={{ paddingTop: '80px' }}>
        {/* Banner */}
        <section style={{ background: 'var(--gradient-hero)', padding: '80px 0 60px', textAlign: 'center' }}>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <h1 style={{ fontSize: '2.5rem', marginBottom: '10px' }}>Get In <span style={{ background: 'var(--gradient-1)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Touch</span></h1>
            <p style={{ color: 'var(--text-secondary)', fontSize: '1.1rem' }}>Have a project in mind? Let's discuss and bring your ideas to life.</p>
          </motion.div>
        </section>

        {/* Contact Section */}
        <section className="section" style={{ background: 'var(--bg-dark)' }}>
          <div className="container">
            <div style={{ display: 'flex', gap: '50px', flexWrap: 'wrap' }}>
              {/* Left - Info */}
              <motion.div style={{ flex: 1, minWidth: '300px' }} initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
                <h2 style={{ fontSize: '1.8rem', marginBottom: '15px', background: 'var(--gradient-1)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Let's Work Together</h2>
                <p style={{ color: 'var(--text-secondary)', lineHeight: '1.8', marginBottom: '30px' }}>
                  I'm always open to discussing new projects, creative ideas, or opportunities. Whether you need a website, web application, or SEO services — feel free to reach out!
                </p>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', marginBottom: '30px' }}>
                  {profile?.location && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                      <div style={{ width: '50px', height: '50px', borderRadius: '15px', background: 'rgba(108,99,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--primary)', fontSize: '1.2rem' }}><FaMapMarkerAlt /></div>
                      <div><p style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>Location</p><p style={{ color: 'var(--text-secondary)' }}>{profile.location}</p></div>
                    </div>
                  )}
                  {profile?.email && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                      <div style={{ width: '50px', height: '50px', borderRadius: '15px', background: 'rgba(255,101,132,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--secondary)', fontSize: '1.2rem' }}><FaEnvelope /></div>
                      <div><p style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>Email</p><p style={{ color: 'var(--text-secondary)' }}>{profile.email}</p></div>
                    </div>
                  )}
                  {profile?.phone && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                      <div style={{ width: '50px', height: '50px', borderRadius: '15px', background: 'rgba(0,210,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent)', fontSize: '1.2rem' }}><FaPhone /></div>
                      <div><p style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>Phone</p><p style={{ color: 'var(--text-secondary)' }}>{profile.phone}</p></div>
                    </div>
                  )}
                </div>

                {/* Social */}
                <h3 style={{ marginBottom: '15px', color: 'var(--text-secondary)' }}>Follow Me</h3>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  {Object.entries(socials || {}).map(([key, url]) => url && socialIcons[key] && (
                    <a key={key} href={url} target="_blank" rel="noreferrer"
                      style={{ width: '45px', height: '45px', borderRadius: '12px', background: 'rgba(108,99,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--primary)', fontSize: '1.2rem', transition: 'all 0.3s', border: '1px solid var(--border)' }}>
                      {socialIcons[key]}
                    </a>
                  ))}
                </div>
              </motion.div>

              {/* Right - Form */}
              <motion.form style={{ flex: 1.2, minWidth: '300px', background: 'var(--bg-card)', padding: '40px', borderRadius: '20px', border: '1px solid var(--border)' }}
                onSubmit={handleSubmit} initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
                <h3 style={{ marginBottom: '25px', color: 'var(--primary)' }}>Send a Message</h3>
                <div style={{ display: 'flex', gap: '15px' }}>
                  <div className="form-group" style={{ flex: 1 }}><label>Your Name *</label><input type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required placeholder="John Doe" /></div>
                  <div className="form-group" style={{ flex: 1 }}><label>Your Email *</label><input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required placeholder="john@example.com" /></div>
                </div>
                <div className="form-group"><label>Subject</label><input type="text" value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} placeholder="Project Inquiry" /></div>
                <div className="form-group"><label>Message *</label><textarea value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} required placeholder="Tell me about your project..." style={{ minHeight: '150px' }} /></div>
                <button type="submit" className="btn btn-primary" disabled={sending} style={{ width: '100%', justifyContent: 'center', padding: '14px' }}>
                  <FaPaperPlane /> {sending ? 'Sending...' : 'Send Message'}
                </button>
              </motion.form>
            </div>
          </div>
        </section>

        <FAQSection />
      </div>
      <Footer profile={profile} />
    </>
  );
};

export default ContactPage;
